<template>
  <div class="executors-page">
    <div class="list-view">
      <div class="toolbar">
        <div class="title-block">
          <h1>Исполнители</h1>
          <span class="counter">{{ executors.length }} / {{ totalCountLabel }}</span>
        </div>

        <div class="controls">
          <button class="btn" type="button" :disabled="isLoading" @click="refreshList">
            {{ isLoading ? 'Обновляем...' : 'Обновить' }}
          </button>
          <button
            :class="['btn', hasDraftExecutor ? 'btn-success' : 'primary']"
            type="button"
            @click="goToWizard"
          >
            {{ hasDraftExecutor ? 'Продолжить создание' : 'Создать' }}
          </button>
        </div>
      </div>

      <ExecutorFilters
        :companies="searchList"
        :loading="isLoading"
        :initial-filters="lastSearchForm"
        @apply="applyFilters"
        @reset="resetFilters"
      />

      <my-loader v-if="isLoading && executors.length === 0" />

      <ExecutorTable
        v-else
        :executors="executors"
        :is-search-active="isSearchActive"
        @page-changed="onPageChanged"
      />
    </div>
  </div>
</template>

<script>
import { computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useStore } from 'vuex'

import ExecutorFilters from '@/pages/securityCheck/components/ExecutorFilters.vue'
import ExecutorTable from '@/pages/securityCheck/components/ExecutorTable.vue'
import { useExecutorWizardStore } from '@/stores/executorWizard'

export default {
  name: 'executorsList',
  components: {
    ExecutorFilters,
    ExecutorTable,
  },
  setup() {
    const store = useStore()
    const router = useRouter()
    const wizard = useExecutorWizardStore()
    const model = 'executors'

    const executors = computed(() => store.state.executors.executors.items.data)
    const searchList = computed(() => store.state.executors.executors.searchList)
    const isLoading = computed(() => store.state.executors.executors.items.loading)
    const isSearchActive = computed(() => store.state.executors.executors.isSearchActive)
    const lastSearchForm = computed(() => store.state.executors.executors.lastSearchForm)
    const currentPage = computed(() => store.state.executors.executors.items.currentPage)
    const totalPages = computed(() => store.state.executors.executors.items.totalPages)

    const hasDraftExecutor = computed(() =>
      Object.values(wizard.selectItems).some(Boolean)
    )

    const totalCountLabel = computed(() => {
      if (isSearchActive.value) {
        return `найдено: ${executors.value.length}, стр. ${currentPage.value} из ${totalPages.value}`
      }
      return `стр. ${currentPage.value} из ${totalPages.value}`
    })

    const loadList = (page = currentPage.value) => {
      store.dispatch('executors/getItems', { params: { page }, model })
    }

    const refreshList = () => {
      if (isSearchActive.value) {
        store.dispatch('executors/reapplySearch', { page: currentPage.value })
        return
      }
      loadList(currentPage.value)
    }

    const goToWizard = () => {
      router.push('/executors/new')
    }

    const applyFilters = (form) => {
      store.dispatch('executors/searchExecutorsFiltered', { form, page: 1 })
    }

    const resetFilters = () => {
      store.dispatch('executors/resetSearch', { page: 1, model })
    }

    const onPageChanged = (page) => {
      if (isSearchActive.value) {
        store.dispatch('executors/reapplySearch', { page })
        return
      }
      loadList(page)
    }

    onMounted(() => {
      store.dispatch('executors/getSearchList', { model })
      loadList(currentPage.value)
    })

    return {
      executors,
      searchList,
      isLoading,
      isSearchActive,
      lastSearchForm,
      totalCountLabel,
      hasDraftExecutor,
      refreshList,
      goToWizard,
      applyFilters,
      resetFilters,
      onPageChanged,
    }
  },
}
</script>

<style scoped>
.executors-page {
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 14px 16px;
  color: #333;
}

.list-view {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 10px;
}

.title-block {
  display: flex;
  align-items: baseline;
  gap: 10px;
}

h1 {
  margin: 0;
  font-size: 22px;
  line-height: 1.2;
}

.counter {
  color: #667085;
  font-size: 13px;
}

.controls {
  display: flex;
  align-items: center;
  gap: 8px;
}

.btn {
  height: 32px;
  border: 1px solid #d7dce2;
  border-radius: 4px;
  background: #fff;
  color: #333;
  cursor: pointer;
  font-size: 13px;
  padding: 0 12px;
}

.btn:hover {
  border-color: #007bff;
  color: #007bff;
}

.btn.primary {
  background: #007bff;
  border-color: #007bff;
  color: #fff;
}

.btn.primary:hover {
  background: #0069d9;
  border-color: #0069d9;
  color: #fff;
}

.btn.btn-success {
  background: #28a745;
  border-color: #28a745;
  color: #fff;
}

.btn.btn-success:hover {
  background: #218838;
  border-color: #1e7e34;
  color: #fff;
}

.btn:disabled {
  cursor: default;
  opacity: 0.6;
}

@media (max-width: 900px) {
  .toolbar {
    align-items: flex-start;
    flex-direction: column;
  }
}
</style>
